<template>
    <section class="tk-insights-workspace">
        <div class="tk-insights-workspace__header">
            <div>
                <h2>Engagement insights</h2>
                <p>Reviewed point-in-time summaries generated from recent eligible case notes.</p>
            </div>
            <q-btn
                unelevated
                no-caps
                flat
                color="primary"
                :icon="overview.draft ? 'edit' : 'auto_awesome'"
                :label="overview.draft ? 'Continue draft' : 'Create insight'"
                :loading="generating"
                :disable="generating || (!overview.draft && !canGenerate)"
                @click="startInsight"
            />
        </div>

        <q-banner v-if="error" rounded class="bg-red-1 text-negative q-mb-md">{{ error }}</q-banner>
        <q-banner v-if="generating" rounded class="bg-blue-1 text-blue-10 q-mb-md">
            <template #avatar><q-spinner color="primary" size="22px" /></template>
            Your insight is generating in the background. You can continue using the application.
        </q-banner>
        <div v-if="loading" class="tk-insights-workspace__loading">
            <q-spinner color="primary" size="30px" />
            <span>Loading insights…</span>
        </div>

        <template v-else>
            <article
                v-if="overview.draft"
                class="tk-insight-list-card tk-insight-list-card--draft"
                role="button"
                tabindex="0"
                @click="openDraft(overview.draft)"
                @keydown.enter.prevent="openDraft(overview.draft)"
                @keydown.space.prevent="openDraft(overview.draft)"
            >
                <div>
                    <TkContextBadge
                        kind="confidential"
                        value="confidential"
                        label="Private draft"
                    />                    
                    <h3>Continue your engagement insight</h3>
                    <p>{{ overview.draft.content.overview }}</p>
                </div>
                <q-icon name="chevron_right" size="24px" />
            </article>

            <q-banner v-if="overview.has_new_case_notes && overview.latest" rounded class="bg-amber-1 text-brown-9 q-mb-md">
                <template #avatar><q-icon name="update" /></template>
                New case notes have been added since the latest insight was generated.
            </q-banner>

            <div v-if="publishedInsights.length" class="tk-insights-workspace__list">
                <article
                    v-for="(insight, index) in publishedInsights"
                    :key="insight.id"
                    class="tk-insight-list-card"
                    role="button"
                    tabindex="0"
                    @click="openPublished(insight)"
                    @keydown.enter.prevent="openPublished(insight)"
                    @keydown.space.prevent="openPublished(insight)"
                >
                    <div>
                        <span class="tk-insight-list-card__eyebrow">{{ index === 0 ? "Latest insight" : "Published insight" }}</span>
                        <h3>{{ formatDateTime(insight.published_at) }}</h3>
                        <p>{{ insight.content.overview }}</p>
                        <span class="tk-insight-list-card__meta">
                            <template v-if="insight.author_name">{{ insight.author_name }} · </template>{{ insight.content.source_coverage }}
                        </span>
                    </div>
                    <q-icon name="chevron_right" size="24px" />
                </article>
            </div>

            <div v-else class="tk-insights-workspace__empty">
                <q-icon name="o_auto_awesome" size="34px" />
                <div>
                    <strong>No published insights yet</strong>
                    <p v-if="canGenerate">Create a private draft from the eligible case notes, then review it before publishing.</p>
                    <p v-else>No eligible non-confidential case notes from the previous 90 days are available.</p>
                </div>
            </div>
        </template>

        <q-dialog v-model="showGenerationReview">
            <q-card class="tk-insights-workspace__dialog">
                <q-card-section class="row items-start justify-between no-wrap">
                    <div>
                        <h2>Create engagement insight</h2>
                        <p>The source selection is automatic and read-only for this version.</p>
                    </div>
                    <q-btn flat round dense icon="close" aria-label="Close" v-close-popup />
                </q-card-section>
                <q-separator />
                <q-card-section>
                    <div class="tk-insights-workspace__coverage">
                        <q-icon name="o_description" color="primary" size="28px" />
                        <div>
                            <strong>{{ sourceCountLabel }}</strong>
                            <span>{{ sourceDateRange }}</span>
                            <span>Confidential notes are excluded.</span>
                        </div>
                    </div>
                    <q-banner rounded class="bg-blue-1 text-blue-10 q-mt-md">
                        AI will create a private draft. Review and edit the narrative before publication.
                    </q-banner>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat no-caps label="Cancel" v-close-popup />
                    <q-btn
                        unelevated
                        no-caps
                        color="primary"
                        icon="auto_awesome"
                        label="Generate private draft"
                        :loading="generating"
                        :disable="!canGenerate"
                        @click="generateDraft"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <TkRightDrawer
            v-model="showDraftDrawer"
            title="Engagement insight draft"
            subtitle="Only you can see this draft until it is published."
            width="920px"
        >
            <TkEngagementInsightDraft
                v-model="editableContent"
                :saving="saving"
                :publishing="publishing"
                @close="showDraftDrawer = false"
                @save="saveDraft"
                @publish="publishDraft"
            />
        </TkRightDrawer>

        <TkRightDrawer
            v-model="showPublishedDrawer"
            title="Published engagement insight"
            subtitle="A reviewed point-in-time summary, not an authoritative current state."
            width="920px"
        >
            <TkEngagementInsightDocument
                v-if="selectedPublished"
                :insight="selectedPublished"
                :person-id="personId"
                :engagement-id="engagementId"
            />
        </TkRightDrawer>
    </section>
</template>

<script>
import api from "@/services/api/api";
import dayjs from "@/services/util/dayjs";
import notify from "@/services/util/notify";
import { copyInsightContent, emptyInsightContent } from "@/services/util/engagementInsights";
import TkEngagementInsightDocument from "@/components/TkClients/TkEngagementInsightDocument.vue";
import TkEngagementInsightDraft from "@/components/TkClients/TkEngagementInsightDraft.vue";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";
import TkContextBadge from "@/components/TkClients/TkContextBadge.vue";

const emptyOverview = () => ({
    draft: null,
    latest: null,
    history: [],
    eligible_source_coverage: { count: 0, start_at: null, end_at: null },
    has_new_case_notes: false,
});

export default {
    name: "TkEngagementInsightsPanel",
    components: {
        TkEngagementInsightDocument,
        TkEngagementInsightDraft,
        TkRightDrawer,
        TkContextBadge,
    },
    emits: ["changed"],
    props: {
        personId: {
            type: Number,
            required: true,
        },
        engagementId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            generating: false,
            saving: false,
            publishing: false,
            error: null,
            overview: emptyOverview(),
            editableContent: emptyInsightContent(),
            selectedPublished: null,
            showGenerationReview: false,
            showDraftDrawer: false,
            showPublishedDrawer: false,
        };
    },
    computed: {
        canGenerate() {
            return this.overview.eligible_source_coverage.count > 0;
        },
        publishedInsights() {
            return [this.overview.latest, ...this.overview.history].filter(Boolean);
        },
        sourceCountLabel() {
            const count = this.overview.eligible_source_coverage.count;
            return `${count} eligible case ${count === 1 ? "note" : "notes"}`;
        },
        sourceDateRange() {
            const coverage = this.overview.eligible_source_coverage;
            if (!coverage.start_at || !coverage.end_at) return "No eligible date range";
            return `${this.formatDate(coverage.start_at)} – ${this.formatDate(coverage.end_at)}`;
        },
    },
    async mounted() {
        await this.loadInsights();
    },
    methods: {
        formatDate(value) {
            return value ? dayjs(value).format("D MMM YYYY") : "";
        },
        formatDateTime(value) {
            return value ? dayjs(value).format("D MMM YYYY, h:mm A") : "";
        },
        errorMessage(error, fallback) {
            const statusText = error?.response?.statusText;
            return statusText && statusText !== "Internal Server Error" ? statusText : fallback;
        },
        async loadInsights() {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.tkPeople.getEngagementInsights(this.personId, this.engagementId);
                this.overview = response.data;
            } catch (error) {
                this.error = this.errorMessage(error, "Failed to load engagement insights.");
            } finally {
                this.loading = false;
            }
        },
        startInsight() {
            if (this.overview.draft) {
                this.openDraft(this.overview.draft);
                return;
            }
            this.showGenerationReview = true;
        },
        openDraft(draft) {
            this.editableContent = copyInsightContent(draft.content);
            this.showDraftDrawer = true;
        },
        openPublished(insight) {
            this.selectedPublished = insight;
            this.showPublishedDrawer = true;
        },
        async generateDraft() {
            this.generating = true;
            this.showGenerationReview = false;
            try {
                const response = await api.tkPeople.generateEngagementInsight(this.personId, this.engagementId);
                this.overview = { ...this.overview, draft: response.data };
                this.$emit("changed");
                notify.primary("Engagement insight draft is ready", "top");
            } catch (error) {
                notify.error(this.errorMessage(error, "Failed to generate engagement insight."), "top");
            } finally {
                this.generating = false;
            }
        },
        async saveDraft() {
            this.saving = true;
            try {
                const response = await api.tkPeople.updateEngagementInsight(
                    this.personId,
                    this.engagementId,
                    this.overview.draft.id,
                    this.editableContent
                );
                this.overview = { ...this.overview, draft: response.data };
                this.editableContent = copyInsightContent(response.data.content);
                this.$emit("changed");
                notify.primary("Engagement insight draft saved", "top");
                return true;
            } catch (error) {
                notify.error(this.errorMessage(error, "Failed to save engagement insight draft."), "top");
                return false;
            } finally {
                this.saving = false;
            }
        },
        async publishDraft() {
            this.publishing = true;
            try {
                const saved = await api.tkPeople.updateEngagementInsight(
                    this.personId,
                    this.engagementId,
                    this.overview.draft.id,
                    this.editableContent
                );
                await api.tkPeople.publishEngagementInsight(this.personId, this.engagementId, saved.data.id);
                this.showDraftDrawer = false;
                await this.loadInsights();
                this.$emit("changed");
                notify.primary("Engagement insight published", "top");
            } catch (error) {
                notify.error(this.errorMessage(error, "Failed to publish engagement insight."), "top");
            } finally {
                this.publishing = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-insights-workspace {
    padding: 18px 24px 30px;
}

.tk-insights-workspace__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
}

.tk-insights-workspace__header h2,
.tk-insights-workspace__dialog h2 {
    margin: 0;
    color: #3d4b46;
    font-size: 1.05rem;
    font-weight: 600;
}

.tk-insights-workspace__header p,
.tk-insights-workspace__dialog p,
.tk-insights-workspace__empty p {
    margin: 5px 0 0;
    color: #748078;
    font-size: 0.84rem;
}

.tk-insights-workspace__loading,
.tk-insights-workspace__empty,
.tk-insights-workspace__coverage {
    display: flex;
    align-items: center;
    gap: 12px;
}

.tk-insights-workspace__loading {
    justify-content: center;
    padding: 30px;
}

.tk-insights-workspace__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tk-insight-list-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 16px;
    border: 1px solid #dfe4e0;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    text-align: left;
}

.tk-insight-list-card--draft {
    margin-bottom: 12px;
    border-color: #e2cd9e;
    background: #fffbf1;
}

.tk-insight-list-card:hover {
    border-color: #98aaa1;
    box-shadow: 0 4px 12px rgba(47, 62, 70, 0.06);
}

.tk-insight-list-card:focus-visible {
    outline: 2px solid #2f6f5e;
    outline-offset: 2px;
}

.tk-insight-list-card h3 {
    margin: 5px 0;
    color: #3d4b46;
    font-size: 0.95rem;
}

.tk-insight-list-card p {
    display: -webkit-box;
    margin: 0;
    color: #53615b;
    font-size: 0.86rem;
    line-height: 1.45;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
}

.tk-insight-list-card__eyebrow,
.tk-insight-list-card__meta {
    color: #748078;
    font-size: 0.74rem;
}

.tk-insight-list-card__meta {
    display: block;
    margin-top: 8px;
}

.tk-insights-workspace__empty {
    padding: 20px;
    border: 1px dashed #cbd4ce;
    border-radius: 8px;
    color: #53645c;
}

.tk-insights-workspace__dialog {
    width: 620px;
    max-width: 94vw;
}

.tk-insights-workspace__coverage div {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

@media (max-width: 700px) {
    .tk-insights-workspace {
        padding: 14px 12px 24px;
    }

    .tk-insights-workspace__header {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
